import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  CaretDown,
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretLeft,
  CaretRight,
  Check
} from '@phosphor-icons/react'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../MuiTablePagination'

function buildPageItems(currentPage, totalPages) {
  const pages = new Set([1, totalPages])
  const start = Math.max(1, Math.min(currentPage - 1, totalPages - 4))
  const end = Math.min(totalPages, Math.max(currentPage + 1, 5))
  for (let page = start; page <= end; page += 1) pages.add(page)
  const sorted = [...pages].sort((a, b) => a - b)
  return sorted.flatMap((page, index) => {
    const previous = sorted[index - 1]
    if (previous && page - previous === 2) return [previous + 1, page]
    return previous && page - previous > 2 ? [`gap-${page}`, page] : [page]
  })
}

/**
 * Paginador global para tablas de escritorio.
 *
 * Datos que recibe:
 * - `pagination` (requerido): objeto con la forma
 *   `{ total_count, per_page, total_pages, current_page }`.
 * - `onPageChange(event, page)` (requerido): se ejecuta al cambiar de página;
 *   la pantalla debe consultar o mostrar los datos de la página indicada.
 * - `onRowsPerPageChange(event, size)` (opcional): se ejecuta al cambiar la
 *   cantidad de registros por página.
 * - `rowsPerPageOptions` (opcional): tamaños disponibles; usa los valores
 *   estándar de la aplicación si no se envía.
 * - `itemLabel`, `emptyLabel` y `ariaLabel`: textos visibles y accesibles.
 * - `showPageSizeControl`: muestra u oculta el selector "Mostrar N por página".
 *   Requiere `onRowsPerPageChange`; sin ese callback el selector se oculta.
 * - `disabled`: bloquea controles mientras se cargan datos.
 */
export default function UbiquaTablePagination({
  ariaLabel = 'Paginación de tabla',
  pagination,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = MUI_TABLE_PAGINATION_OPTIONS,
  itemLabel = 'registros',
  emptyLabel = 'Sin registros para mostrar',
  showPageSizeControl = true,
  disabled = false
}) {
  const sizeId = useId()
  const sizeMenuId = useId()
  const [sizeMenuOpen, setSizeMenuOpen] = useState(false)
  const sizeControlRef = useRef(null)
  const sizeTriggerRef = useRef(null)
  const sizeOptionRefs = useRef([])
  const totalCount = Math.max(0, Number(pagination?.total_count) || 0)
  const perPage = Math.max(1, Number(pagination?.per_page) || rowsPerPageOptions[0])
  const totalPages = Math.max(1, Number(pagination?.total_pages) || Math.ceil(totalCount / perPage))
  const currentPage = Math.min(totalPages, Math.max(1, Number(pagination?.current_page) || 1))
  const rangeStart = totalCount ? (currentPage - 1) * perPage + 1 : 0
  const rangeEnd = Math.min(currentPage * perPage, totalCount)
  const canChangePageSize = typeof onRowsPerPageChange === 'function'
  const pageItems = useMemo(
    () => buildPageItems(currentPage, totalPages),
    [currentPage, totalPages]
  )

  useEffect(() => {
    if (!sizeMenuOpen) return undefined

    const closeOnOutsideClick = (event) => {
      if (!sizeControlRef.current?.contains(event.target)) setSizeMenuOpen(false)
    }
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return
      setSizeMenuOpen(false)
      sizeTriggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [sizeMenuOpen])

  const focusSizeOption = (index) => {
    window.requestAnimationFrame(() => sizeOptionRefs.current[index]?.focus())
  }

  const openSizeMenu = () => {
    setSizeMenuOpen(true)
    const selectedIndex = Math.max(
      0,
      rowsPerPageOptions.findIndex((size) => Number(size) === perPage)
    )
    focusSizeOption(selectedIndex)
  }

  const choosePageSize = (event, size) => {
    setSizeMenuOpen(false)
    onRowsPerPageChange?.(event, Number(size))
    sizeTriggerRef.current?.focus()
  }

  const handleSizeOptionKeyDown = (event, index) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
    event.preventDefault()
    if (event.key === 'Home') return focusSizeOption(0)
    if (event.key === 'End') return focusSizeOption(rowsPerPageOptions.length - 1)
    const direction = event.key === 'ArrowDown' ? 1 : -1
    const nextIndex = (index + direction + rowsPerPageOptions.length) % rowsPerPageOptions.length
    return focusSizeOption(nextIndex)
  }

  const goToPage = (page) => {
    if (!disabled && page >= 1 && page <= totalPages && page !== currentPage)
      onPageChange?.(null, page)
  }

  return (
    <nav className="ubiqua-table-pagination" aria-label={ariaLabel} aria-busy={disabled}>
      <div className="ubiqua-table-page-summary">
        <span aria-live="polite">
          {totalCount ? (
            <>
              <strong>
                {rangeStart.toLocaleString('es')}–{rangeEnd.toLocaleString('es')}
              </strong>{' '}
              de <strong>{totalCount.toLocaleString('es')}</strong> {itemLabel}
            </>
          ) : (
            emptyLabel
          )}
        </span>
        {showPageSizeControl && canChangePageSize ? (
          <div className="ubiqua-table-page-size">
            <label htmlFor={sizeId}>Mostrar</label>
            <span className="ubiqua-table-page-size-control" ref={sizeControlRef}>
              <button
                id={sizeId}
                ref={sizeTriggerRef}
                type="button"
                className="ubiqua-table-page-size-trigger"
                aria-label="Registros por página"
                aria-haspopup="listbox"
                aria-expanded={sizeMenuOpen}
                aria-controls={sizeMenuOpen ? sizeMenuId : undefined}
                disabled={disabled}
                onClick={() => {
                  if (sizeMenuOpen) setSizeMenuOpen(false)
                  else openSizeMenu()
                }}
                onKeyDown={(event) => {
                  if (!sizeMenuOpen && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
                    event.preventDefault()
                    openSizeMenu()
                  }
                }}
              >
                <span>{perPage}</span>
                <CaretDown size={12} aria-hidden="true" />
              </button>
              {sizeMenuOpen ? (
                <div
                  id={sizeMenuId}
                  className="ubiqua-table-page-size-menu"
                  role="listbox"
                  aria-label="Registros por página"
                >
                  {rowsPerPageOptions.map((size, index) => {
                    const selected = Number(size) === perPage
                    return (
                      <button
                        key={size}
                        ref={(node) => {
                          sizeOptionRefs.current[index] = node
                        }}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={selected ? 'is-selected' : undefined}
                        disabled={disabled}
                        onClick={(event) => choosePageSize(event, size)}
                        onKeyDown={(event) => handleSizeOptionKeyDown(event, index)}
                      >
                        <span>{size}</span>
                        {selected ? <Check size={13} weight="bold" aria-hidden="true" /> : null}
                      </button>
                    )
                  })}
                </div>
              ) : null}
            </span>
            <span className="ubiqua-table-page-size-suffix">por página</span>
          </div>
        ) : null}
      </div>
      <div className="ubiqua-table-page-navigation">
        <span className="ubiqua-table-page-position">
          Página <strong>{currentPage}</strong> de {totalPages}
        </span>
        <div className="ubiqua-table-page-buttons">
          <button
            type="button"
            className="ubiqua-table-page-edge"
            aria-label="Primera página"
            disabled={disabled || currentPage === 1}
            onClick={() => goToPage(1)}
          >
            <CaretDoubleLeft size={15} />
          </button>
          <button
            type="button"
            aria-label="Página anterior"
            disabled={disabled || currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            <CaretLeft size={15} />
          </button>
          {pageItems.map((page) =>
            typeof page === 'string' ? (
              <span key={page} className="ubiqua-table-page-gap" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={page}
                type="button"
                aria-label={`Página ${page}`}
                data-compact-hidden={Math.abs(page - currentPage) > 1 || undefined}
                aria-current={page === currentPage ? 'page' : undefined}
                className={page === currentPage ? 'is-active' : undefined}
                disabled={disabled}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            type="button"
            aria-label="Página siguiente"
            disabled={disabled || currentPage === totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            <CaretRight size={15} />
          </button>
          <button
            type="button"
            className="ubiqua-table-page-edge"
            aria-label="Última página"
            disabled={disabled || currentPage === totalPages}
            onClick={() => goToPage(totalPages)}
          >
            <CaretDoubleRight size={15} />
          </button>
        </div>
      </div>
    </nav>
  )
}
